import {combineLatest} from 'rxjs';
import {map} from 'rxjs/operators';
import {timeUtil} from '@/helper/time';
import {numUtil} from '@/helper/num';
import {orderUtil} from '@/helper/order';
import {qtyUtil} from '@/helper/qty-type-contract';
import {contracts$} from '@/config';
import {isTrailingStopOrder} from '@/tpslts/order-filters';
import {contractHistoryOrdersCache$} from './orders-history-contract-cache';
import type {ws, ContractHistoryOrder, ContractPair} from '@/types';
type ContractOrderRaw = ws.ContractOrderRaw;

export type Cache = Map<string, ContractOrderRaw>;

export const contractHistoryOrders$ = combineLatest([contractHistoryOrdersCache$, contracts$]).pipe<
  ContractHistoryOrder[]
>(
  map(([cache, contracts]) =>
    cache
      .toArray((o) => convertToHistoryOrder(o, contracts))
      .filter(Boolean)
      .sort((a, b) => b.transactTimestamp - a.transactTimestamp)
  )
);

function calcPrice(raw, precision, priceScale) {
  const isMarket = orderUtil.isMarketOrder(raw.ordType);
  if (isMarket) {
    return 'Market';
  }
  if (raw.priceEp === 0) {
    return '-';
  }
  return numUtil.delimit(numUtil.floor(raw.priceEp, precision, priceScale));
}

function calcFillPrice(raw, contractSide, factor, contractSize, precision) {
  if (raw.cumValueEv === 0 || raw.cumQty === 0) {
    return '-';
  }
  return numUtil.delimit(
    numUtil.floor(Math.pow(raw.cumValueEv / (raw.cumQty * factor * contractSize), contractSide), precision)
  );
}

function calcStopPx(ordType, stopPxEp, precision, priceScale) {
  if (orderUtil.isConditionalOrder(ordType)) {
    return stopPxEp === 0 ? '-' : numUtil.delimit(numUtil.floor(stopPxEp, precision, priceScale));
  }

  return '-';
}

function convertToHistoryOrder(raw: ContractOrderRaw, contracts: ContractPair[]): ContractHistoryOrder {
  const contract = contracts.find((o) => o.symbol === raw.symbol);
  if (!contract) {
    return null;
  }

  const {displaySymbol, contractSide, contractSize, pricePrecision, valueFactor, priceScale, pairName} = contract;
  const {
    orderID,
    clOrdID,
    ordType,
    ordStatus,
    symbol,
    side,
    priceEp,
    stopPxEp,
    orderQty,
    cumQty,
    leavesQty,
    createBy,
    actionTimeNs,
    transactTimeNs,
    timeInForce,
    action,
    execPriceEp,
    posSide,
    pegPriceType,
    orderListStatus,
    orderListType,
    orderListId,
  } = raw as any;
  const actionTimestamp = Math.floor(actionTimeNs / 1e6);
  const transactTimestamp = Math.floor(transactTimeNs / 1e6);
  const orderQtyStr = orderUtil.formatOrderQty(orderQty);

  const execPrice = numUtil.delimit(numUtil.floor(execPriceEp, pricePrecision, priceScale));
  const fillPrice = calcFillPrice(raw, contractSide, valueFactor, contractSize, pricePrecision);
  const qtyPrice = getQtyPrice(raw, contract);
  const ordType2 = action === 'LiqRequest' ? 'Liquidation' : ordType;

  return <ContractHistoryOrder>{
    orderID,
    clOrdID,
    ordType: ordType2,
    displayOrdType: isTrailingStopOrder(pegPriceType, ordType) && action !== 'LiqRequest' ? 'TrailingStop' : ordType2,
    ordStatus,
    displaySymbol,
    symbol,
    pairName,
    side,
    orderQty,
    orderQtyStr,
    orderQtyStrByCont: qtyUtil.toCont(contract, orderQty),
    orderQtyStrByValue: qtyUtil.toValue(contract, orderQty, qtyPrice),
    orderQtyStrByCoin: qtyUtil.toCoin(contract, orderQty, qtyPrice),
    cumQty,
    cumQtyByCont: qtyUtil.toCont(contract, cumQty),
    cumQtyByValue: qtyUtil.toValue(contract, cumQty, qtyPrice),
    cumQtyByCoin: qtyUtil.toCoin(contract, cumQty, qtyPrice),
    leavesQty,
    leavesQtyByCont: qtyUtil.toCont(contract, leavesQty),
    leavesQtyByValue: qtyUtil.toValue(contract, leavesQty, qtyPrice),
    leavesQtyByCoin: qtyUtil.toCoin(contract, leavesQty, qtyPrice),
    leavesQtyStr: numUtil.delimit(leavesQty),
    priceEp,
    price: calcPrice(raw, pricePrecision, priceScale),
    fillPrice,
    stopPxEp,
    stopPx: calcStopPx(ordType, stopPxEp, pricePrecision, priceScale),
    createBy,
    actionTimestamp,
    actionTime: timeUtil.formatDateTime(actionTimestamp),
    actionMonthDayTime: timeUtil.formatMonthDayTime(actionTimestamp),
    transactTimestamp,
    transactTime: timeUtil.formatDateTime(transactTimestamp),
    transactMonthDayTime: timeUtil.formatMonthDayTime(transactTimestamp),
    timeInForce,
    execPriceEp,
    execPrice,
    posSide,
    orderListStatus,
    orderListType,
    orderListId,
  };
}

function getQtyPrice(raw, contract) {
  const {pricePrecision, priceScale} = contract;
  const priorityKeys = ['execPriceEp', 'priceEp', 'stopPxEp'];

  for (let i = 0; i < priorityKeys.length; i++) {
    const key = priorityKeys[i];
    const price = raw[key];
    if (price) {
      return numUtil.floor(price, pricePrecision, priceScale);
    }
  }
  return '';
}
